"use client";

import { formatUnits } from "viem";

export interface VoteCountBadgeProps {
  /**
   * Total received-vote DNM in wei (sentinel-subtracted).
   * Pass 0n for never-voted-on candidates.
   */
  voteCountRaw: bigint;
  /**
   * Width of the digit area in `ch` units. Lists pre-compute the longest
   * formatted number across all rows and pass it down so every badge in the
   * list shares the same width — uniform alignment, no clipping, scales with
   * the actual data in view. Falls back to 4ch (≈ "0.00") when no caller
   * supplied a value.
   */
  digitsCh?: number;
}

/**
 * Vote count chip — Figma node 5462:51639/51658/51677.
 * Same shell as RankBadge: bg-bg-tertiary60, w=54 h=48, rounded-tr-sm rounded-br-sm.
 * Number Peyda Semi Bold 18, "DNM" Peyda Medium 12.
 *
 * r5 S4: accepts bigint (wei); displays formatUnits(_, 18) with trailing zero strip.
 * r7: stacked layout — number (Semi Bold 18) on top, "DNM" label (Medium 12) below.
 * Figma "123↵DNM" two-line style.
 */
/**
 * Cap decimal display at 2 places so long-tail dust amounts (e.g. 100.0108)
 * don't blow past the badge width. Trailing zeros are stripped so 100.00 → 100,
 * 1.50 → 1.5. Whole-number values stay whole. Exported so list components can
 * pre-compute the longest formatted string and pass `digitsCh` to align tiles.
 */
export function formatVoteCount(wei: bigint): string {
  const raw = formatUnits(wei, 18);
  const dotIx = raw.indexOf(".");
  if (dotIx === -1) return raw;
  const trimmed = `${raw.slice(0, dotIx)}.${raw.slice(dotIx + 1, dotIx + 3)}`;
  return trimmed.replace(/(\.\d*?)0+$/, "$1").replace(/\.$/, "");
}

const DEFAULT_DIGITS_CH = 4;

export const VoteCountBadge: React.FC<VoteCountBadgeProps> = ({
  voteCountRaw,
  digitsCh = DEFAULT_DIGITS_CH,
}) => {
  const display = formatVoteCount(voteCountRaw);
  // tabular-nums forces equal-width digits so badges sized in `ch` units
  // remain visually aligned even when characters have different intrinsic
  // widths (e.g. 1 vs 0 in many proportional fonts).
  return (
    <div
      className="flex h-12 flex-col items-center justify-center gap-0.5 rounded-br-sm rounded-tr-sm bg-bg-tertiary/60 px-3 text-text-primary"
      style={{ width: `calc(${digitsCh}ch + 24px)` }}
    >
      <span
        className="text-lg font-semibold leading-[1.4] tabular-nums"
        title={`${formatUnits(voteCountRaw, 18)} DNM`}
      >
        {display}
      </span>
      <span className="text-xs font-medium leading-[1.4]">DNM</span>
    </div>
  );
};
